import { useLocalization } from '@/hooks/useLocalization';
import ConnectToFinancialInstitution from './accountModal/ConnectToFinancialInstitution/ConnectToFinancialInstitution';
import useAddAccountNavigation, {
  FinancialOptions,
  StepsForManual,
  StepsForRecommended,
} from './accountModal/useAddAccountNavigation';
import RecommendedYodleeIntegration from './accountModal/RecommendedYodleeIntegration/RecommendedYodleeIntegration';
import RecommendedAccountBeingAdded from './accountModal/RecommendedAccountBeingAdded/RecommendedAccountBeingAdded';
import useInstitutionSelection from './accountModal/useInstitutionSelection';
import ManualSelectAccountType from './accountModal/ManualSelectAccount/ManualSelectAccountType';
import ManualAddAccountDetails from './accountModal/ManualAccountDetails/ManualAddAccountDetails';
import useAccountDetailsForm from './accountModal/ManualAccountDetails/useAccountDetailsForm';
import { useMemo, useRef, useState } from 'react';
import { useGetClassificationsQuery } from '@/store/classificationsApi';
import ManualPortfolioDiversification from './accountModal/ManualPortfolioDiversification/ManualPortfolioDiversification';
import usePortfolioDiversificationForm from './accountModal/ManualPortfolioDiversification/usePortfolioDiversificationForm';
import ManualHistoricalValues from './accountModal/ManualHistoricalValues/ManualHistoricalValues';
import useHistoricalValuesForm from './accountModal/ManualHistoricalValues/useHistoricalValuesForm';
import useAccountSave from './accountModal/useAccountSave';
import useExpandableList from './accountModal/ManualSelectAccount/useExpandableList';
import ManualAddRealEstateDetails from './accountModal/ManualRealEstateDetails/ManualRealEstateDetails';
import useRealEstateDetailsForm from './accountModal/ManualRealEstateDetails/useRealEstateDetailsForm';
import AccountModal from './accountModal/shared/AccountModal';
import usePortfolioDiversificationCategories from './accountModal/ManualPortfolioDiversification/usePortfolioDiversificationCategories';
import ManualAccountBeingAdded, {
  ManualSaveStatus,
} from './accountModal/ManualAccountBeingAdded/ManualAccountBeingAdded';
import { useGlobalSnackbar } from '@/contexts/snackContext';
import { useDispatch } from 'react-redux';
import { accountsApi } from '@/store/accountsApi';
import { diversificationsApi } from '@/store/diversificationsApi';

interface AddAccountModalProps {
  open: boolean;
  onClose: () => void;
}

const AddAccountModal = ({ open, onClose }: AddAccountModalProps) => {
  const { t } = useLocalization();
  const { showSnackbar } = useGlobalSnackbar();
  const dispatch = useDispatch();

  const { data: classificationsResponse, isLoading: isClassificationsLoading } =
    useGetClassificationsQuery();

  // Prefetch data since diversifications can be 1-3 dropdowns.
  usePortfolioDiversificationCategories();

  const { modalExperience, modalStep, updateModalExperience, updateModalStep } =
    useAddAccountNavigation();
  const { updateSelected, selected } = useInstitutionSelection();

  const {
    accountDetailsForm,
    updateAccountDetailForm,
    resetAccountDetailForm,
  } = useAccountDetailsForm();

  const {
    realEstateDetailsForm,
    updateRealEstateDetailForm,
    resetRealEstateDetailsForm,
  } = useRealEstateDetailsForm();

  const {
    portfolioDiversificationForm,
    resetPortfolioDiversificationForm,
    updatePortfolioDiversificationForm,
  } = usePortfolioDiversificationForm();

  const {
    historicalValuesForm,
    updateHistoricalValuesForm,
    resetHistoricalValuesForm,
  } = useHistoricalValuesForm();

  const classifications = useMemo(() => {
    return classificationsResponse?.Records ?? [];
  }, [classificationsResponse]);

  const {
    handleCategoryClick,
    openCategories,
    accountTypes,
    selectedClassification,
    handleClassificationClick,
  } = useExpandableList(classifications);

  const resetForms = () => {
    handleClassificationClick(null);
    resetAccountDetailForm();
    resetRealEstateDetailsForm();
    resetPortfolioDiversificationForm();
    resetHistoricalValuesForm();
  };

  const invalidateAccountsList = () => {
    dispatch(
      accountsApi.util.invalidateTags([{ type: 'Accounts', id: 'LIST' }]),
    );
  };

  /** Ensures balance sheet, dashboard, and portfolio views refetch after add-account. */
  const handleModalClose = () => {
    updateModalExperience(FinancialOptions.ADD_ACCOUNT);
    updateModalStep(null);
    invalidateAccountsList();
    onClose();
  };

  /**
   * When a user clicks "Cancel", return them to the
   * "Connect to your financial institution" screen.
   */
  const handleOnCancel = () => {
    updateModalExperience(FinancialOptions.ADD_ACCOUNT);
    updateModalStep(null);

    resetForms();
  };

  const { save } = useAccountSave();

  // Guard against double-submit. The Save button is only validity-gated and the
  // step advances optimistically, so a rapid double-click previously fired two
  // create requests and produced duplicate accounts. Setting the ref synchronously
  // (before the first await) is what actually blocks the second POST. saveStatus
  // drives the button spinner and the "being added" screen's progress indicator,
  // so the spinner resolves to a result instead of spinning forever.
  const isSavingRef = useRef(false);
  const [saveStatus, setSaveStatus] = useState<ManualSaveStatus>('idle');

  const handleManualSave = async (): Promise<boolean> => {
    if (isSavingRef.current || !selectedClassification) {
      return false;
    }
    isSavingRef.current = true;
    setSaveStatus('saving');

    const name =
      accountDetailsForm.values.Name ||
      realEstateDetailsForm.values.Name ||
      selectedClassification.Name;

    const request = {
      accountType: selectedClassification,
      historicalValuesForm,
      portfolioDiversificationForm,
      accountDetailsForm,
      realEstateDetailsForm,
    };

    resetForms();

    try {
      await save(request);

      invalidateAccountsList();
      dispatch(
        diversificationsApi.util.invalidateTags([
          { type: 'Diversification', id: 'LIST' },
        ]),
      );

      setSaveStatus('success');
      showSnackbar(
        t('addAccountModal.submission.success', {
          name,
        }),
      );
      return true;
    } catch (error: unknown) {
      if (import.meta.env.DEV) {
        const cause =
          error instanceof Error && 'cause' in error
            ? (error as Error & { cause?: unknown }).cause
            : undefined;
        console.error('[addAccountModal] handleManualSave failed', {
          name,
          error,
          cause,
        });
      }
      setSaveStatus('error');
      showSnackbar(
        t('addAccountModal.submission.error', {
          name,
        }),
      );
      return false;
    } finally {
      isSavingRef.current = false;
    }
  };

  const footerLabels = {
    save: t('addAccountModal.footer.save'),
    back: t('addAccountModal.footer.back'),
    continue: t('addAccountModal.footer.continue'),
    cancel: t('addAccountModal.footer.cancel'),
  };

  const modalTitle = useMemo(() => {
    if (modalExperience === FinancialOptions.RECOMMENDED && selected) {
      return t('addAccountModal.title', { institutionName: selected.label });
    }
    return t('addAccountModal.titleDefault');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalExperience, selected]);

  // Helper function to determine if back button should show and its handler.
  // Only used on the yodlee screen.
  const getBackButtonHandler = () => {
    if (
      modalExperience === FinancialOptions.RECOMMENDED &&
      modalStep === StepsForRecommended.RECOMMENDED_SELECTION
    ) {
      return () => {
        updateModalExperience(FinancialOptions.ADD_ACCOUNT);
        updateModalStep(null);
      };
    }
    return undefined;
  };

  const renderCurrentContent = () => {
    // Base selection, always the first option
    if (modalExperience === FinancialOptions.ADD_ACCOUNT) {
      return (
        <ConnectToFinancialInstitution
          updateSelected={updateSelected}
          updateModalExperience={updateModalExperience}
          updateModalStep={updateModalStep}
          handleClassificationClick={handleClassificationClick}
          classifications={classifications}
          isLoading={isClassificationsLoading}
        />
      );
    }

    if (modalExperience === FinancialOptions.RECOMMENDED) {
      if (modalStep === StepsForRecommended.RECOMMENDED_SELECTION) {
        return (
          <RecommendedYodleeIntegration
            selected={selected}
            updateSelected={updateSelected}
            updateModalStep={updateModalStep}
            updateModalExperience={updateModalExperience}
            onClose={handleModalClose}
          />
        );
      }

      if (modalStep === StepsForRecommended.ACCOUNT_ADDED) {
        return (
          <RecommendedAccountBeingAdded
            updateModalExperience={updateModalExperience}
            selected={selected}
            onClose={handleModalClose}
          />
        );
      }
    }

    if (modalExperience === FinancialOptions.MANUAL) {
      if (modalStep === StepsForManual.MANUAL_ACCOUNT_TYPE) {
        return (
          <ManualSelectAccountType
            handleOnCancel={handleOnCancel}
            updateModalStep={updateModalStep}
            footerLabels={footerLabels}
            classifications={classifications}
            accountTypes={accountTypes}
            selectedClassification={selectedClassification}
            handleClassificationClick={handleClassificationClick}
            handleCategoryClick={handleCategoryClick}
            openCategories={openCategories}
          />
        );
      }

      // The "account being added" screen runs after resetForms() has cleared the
      // selection, so handle it before narrowing selectedClassification below.
      if (modalStep === StepsForManual.MANUAL_ACCOUNT_ADDED) {
        return (
          <ManualAccountBeingAdded
            updateModalExperience={updateModalExperience}
            onClose={handleModalClose}
            saveStatus={saveStatus}
          />
        );
      }

      // Every remaining manual step requires a chosen classification.
      if (!selectedClassification) {
        return null;
      }

      if (modalStep === StepsForManual.MANUAL_REAL_ESTATE_DETAILS) {
        return (
          <ManualAddRealEstateDetails
            handleOnCancel={handleOnCancel}
            updateModalStep={updateModalStep}
            footerLabels={footerLabels}
            selectedClassification={selectedClassification}
            updateRealEstateDetailForm={updateRealEstateDetailForm}
            realEstateDetailsForm={realEstateDetailsForm}
            resetRealEstateDetailsForm={resetRealEstateDetailsForm}
            classifications={classifications}
          />
        );
      }

      if (modalStep === StepsForManual.MANUAL_ACCOUNT_DETAILS) {
        return (
          <ManualAddAccountDetails
            handleOnCancel={handleOnCancel}
            updateModalStep={updateModalStep}
            footerLabels={footerLabels}
            selectedClassification={selectedClassification}
            updateAccountDetailForm={updateAccountDetailForm}
            accountDetailsForm={accountDetailsForm}
            classifications={classifications}
          />
        );
      }

      if (modalStep === StepsForManual.MANUAL_PORTFOLIO_DIVERSIFICATION) {
        return (
          <ManualPortfolioDiversification
            handleOnCancel={handleOnCancel}
            footerLabels={footerLabels}
            updateModalStep={updateModalStep}
            selectedClassification={selectedClassification}
            portfolioDiversificationForm={portfolioDiversificationForm}
            updatePortfolioDiversificationForm={
              updatePortfolioDiversificationForm
            }
            realEstateDetailsForm={realEstateDetailsForm}
          />
        );
      }

      if (modalStep === StepsForManual.MANUAL_HISTORICAL_VALUES) {
        return (
          <ManualHistoricalValues
            handleOnCancel={handleOnCancel}
            footerLabels={footerLabels}
            accountDetailsForm={accountDetailsForm}
            realEstateDetailsForm={realEstateDetailsForm}
            portfolioDiversificationForm={portfolioDiversificationForm}
            historicalValuesForm={historicalValuesForm}
            updateHistoricalValuesForm={updateHistoricalValuesForm}
            selectedClassification={selectedClassification}
            updateModalStep={updateModalStep}
            handleSave={handleManualSave}
            isSaving={saveStatus === 'saving'}
          />
        );
      }
    }

    return null;
  };

  return (
    <AccountModal
      title={modalTitle}
      onHeaderBack={getBackButtonHandler()}
      dialogContent={renderCurrentContent()}
      onClose={handleModalClose}
      open={open}
    />
  );
};

export default AddAccountModal;
